// @ts-check
// ── Session requests: the words the coach reads ──────────────────────────────────────────────────────────────────────
//
// Session requests (Clayton, 10/16). With approval on, a client asks for a time and it waits for the coach's OK on that
// client's Session Tracker. Every sentence the coach reads about a request is made here, with no page, no network and
// no clock of its own, so the same words are testable and the card, the Decline window and the two questions the
// server can ask all say a time the same way.
//
// ⚠️ A TIME IS SAID IN THE COACH'S ZONE, NEVER THE BROWSER'S. The request carries an instant (`requestedStart`); "Tue,
// Oct 21 at 3:00 PM" is only true in the zone the coach's hours run in (config.js getCoachTimezone, the one zone the
// whole CRM uses). Callers pass it in.
//
// ⚠️ BUILT FROM PARTS, NOT toLocaleString. Some releases of the browser's locale data (CLDR 42, 2022) put a narrow
// no-break space before "PM" in the built-in time format and others do not, so a sentence made that way can differ from
// one machine to the next, and from the one the guide shows. Intl's parts are joined with ordinary spaces here.

/**
 * @typedef {{ id: string, clientId: string, clientName?: string, kind: string, sessionId?: string|null,
 *   fromStart?: string|null, requestedStart: string, requestedDay?: string, durationMin?: number, note?: string,
 *   status: string, createdAt?: string|null, warnings?: string[] }} SessionRequest
 *   what coach-session-requests-list.js answers for one waiting request
 */

/**
 * The parts of an instant in a zone, or null when it is not a time.
 * @param {string|number|Date|null|undefined} instant @param {string} tz
 * @returns {{ weekday: string, month: string, day: string, year: string, hour: string, minute: string, dayPeriod: string }|null}
 */
function partsIn(instant, tz) {
  const d = new Date(/** @type {any} */ (instant));
  if (!instant || Number.isNaN(d.getTime())) return null;
  /** @type {Record<string, string>} */
  const out = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d)) out[p.type] = p.value;
  return /** @type {any} */ (out);
}

/** The year of an instant in a zone, so a date in another year says so. @param {string|number|Date} now @param {string} tz */
const yearOf = (now, tz) => (partsIn(now, tz) || { year: '' }).year;

/**
 * "Tue, Oct 21" (the year too, only when it is not this year).
 * @param {string} iso @param {string} tz @param {string|number|Date} [now]
 */
export function dayWords(iso, tz, now = Date.now()) {
  const p = partsIn(iso, tz);
  if (!p) return '';
  return `${p.weekday}, ${p.month} ${p.day}${p.year !== yearOf(now, tz) ? `, ${p.year}` : ''}`;
}

/** "3:00 PM". @param {string} iso @param {string} tz */
export function timeWords(iso, tz) {
  const p = partsIn(iso, tz);
  return p ? `${p.hour}:${p.minute} ${p.dayPeriod}` : '';
}

/** "Tue, Oct 21 at 3:00 PM", or "this time" when it cannot be read, so a sentence around it still reads. @param {string} iso @param {string} tz @param {string|number|Date} [now] */
export function whenWords(iso, tz, now = Date.now()) {
  const day = dayWords(iso, tz, now);
  return day ? `${day} at ${timeWords(iso, tz)}` : 'this time';
}

/**
 * The row's headline: the time asked for, or for a move, the session's old time and the new one. ⚠️ BOTH DAYS ARE
 * SAID (Thu, Oct 22 and Fri, Oct 23), not just "Thu 9:00 AM": a request can be weeks ahead, and a weekday alone could
 * be any of them.
 * @param {Pick<SessionRequest, 'kind'|'fromStart'|'requestedStart'>} r @param {string} tz @param {string|number|Date} [now]
 */
export function requestTitle(r, tz, now = Date.now()) {
  if (r.kind === 'move') {
    return r.fromStart
      ? `Move ${whenWords(r.fromStart, tz, now)} to ${whenWords(r.requestedStart, tz, now)}`
      : `Move to ${whenWords(r.requestedStart, tz, now)}`;
  }
  return whenWords(r.requestedStart, tz, now);
}

/** "Asked Oct 16": the day the client asked, in the coach's zone. '' when it is not known. @param {string|null|undefined} createdAt @param {string} tz @param {string|number|Date} [now] */
export function askedWords(createdAt, tz, now = Date.now()) {
  const p = partsIn(createdAt, tz);
  if (!p) return '';
  return `Asked ${p.month} ${p.day}${p.year !== yearOf(now, tz) ? `, ${p.year}` : ''}`;
}

/** The Decline window's question. @param {Pick<SessionRequest, 'kind'|'requestedStart'>} r @param {string} tz @param {string|number|Date} [now] */
export function declineTitle(r, tz, now = Date.now()) {
  return r.kind === 'move'
    ? `Decline the move to ${whenWords(r.requestedStart, tz, now)}?`
    : `Decline ${whenWords(r.requestedStart, tz, now)}?`;
}

/** What the number of waiting requests says. @param {number} n */
export function waitingCount(n) {
  return `${n} waiting`;
}

// ── The Booking choice on one client's Session Tracker ───────────────────────

/**
 * The three answers to "does a booking from this client wait for my OK". `wide` is the coach's switch for everyone
 * (Settings → Sessions → Online Booking), which the first answer follows and names; null while it has not been read.
 * @param {boolean|null} wide
 * @returns {{ value: 'default'|'ask'|'direct', label: string }[]}
 */
export function approvalChoices(wide) {
  return [
    { value: 'default', label: wide === true ? 'Your default (needs your OK)' : wide === false ? 'Your default (books straight in)' : 'Your default' },
    { value: 'ask', label: 'Always needs your OK' },
    { value: 'direct', label: 'Books straight in' },
  ];
}

/**
 * Which answer a client is on. ⚠️ STRICT, like the server (_shared/session-approval.js): only a real true or false is
 * the client's own answer; a null, a missing key and a stray string all follow the coach's default.
 * @param {{ sessionApproval?: unknown }} client @returns {'default'|'ask'|'direct'}
 */
export function approvalValue(client) {
  if (client.sessionApproval === true) return 'ask';
  if (client.sessionApproval === false) return 'direct';
  return 'default';
}

/**
 * What the select's value is saved as (client-session-approval-set): true, false or null. undefined for a value that is
 * not one of the three, which the caller refuses rather than sending.
 * @param {string} value @returns {true|false|null|undefined}
 */
export function approvalToSend(value) {
  if (value === 'ask') return true;
  if (value === 'direct') return false;
  if (value === 'default') return null;
  return undefined;
}

// ── The two questions the server can ask while approving ─────────────────────

/**
 * What already sits in the hour, as one phrase ("Sam's session at 3:00 PM and your time off"). The conflicts are
 * coach-session-request-decide's: session, meeting, google, weekly (another client's weekly time), time_off.
 * @param {any[]} conflicts @param {string} tz
 */
export function conflictWords(conflicts, tz) {
  /** @param {any} c */
  const one = (c) => {
    const at = c && c.startUTC ? ` at ${timeWords(c.startUTC, tz)}` : '';
    switch (c && c.kind) {
      case 'session': return c.who ? `${c.who}'s session${at}` : `another session${at}`;
      case 'meeting': return `${c.title || 'a call'}${at}`;
      case 'google': return `something on your calendar${at}`; // never "Google" (10/17: our calendar is the main one)
      case 'weekly': return c.who ? `${c.who}'s weekly time` : "another client's weekly time";
      case 'time_off': return 'your time off';
      default: return 'something else on your calendar';
    }
  };
  const parts = [...new Set((conflicts || []).map(one))];
  if (!parts.length) return 'something already on your calendar';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** "This clashes with … Approve anyway?" @param {any[]} conflicts @param {string} tz */
export function clashQuestion(conflicts, tz) {
  return `This clashes with ${conflictWords(conflicts, tz)}. Approve anyway?`;
}

/** "Mickey has no free session for this. Book it as a comp?" @param {string} firstName */
export function compQuestion(firstName) {
  return `${firstName || 'This client'} has no free session for this. Book it as a comp?`;
}

/** The words after an answer landed. @param {'approve'|'decline'} decision @param {string} kind @param {string} firstName */
export function answeredWords(decision, kind, firstName) {
  const who = firstName || 'The client';
  if (decision === 'decline') return `Declined. ${who} will be told.`;
  return kind === 'move' ? `Approved. ${who}'s session is moved.` : `Approved. ${who} is booked in.`;
}
